import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { i18n } from '@/i18n'

// `meta.title` è una CHIAVE i18n, non un testo: il titolo della scheda del browser è l'unico
// pezzo di interfaccia che non passa da un template.
const routes: RouteRecordRaw[] = [
  {
    path: '/auth',
    component: () => import('@/components/layout/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: { title: 'routes.login' },
      },
    ],
  },
  {
    // Tutto ciò che sta dentro l'app richiede il login: `requiresAuth` sul genitore lo copre
    // per ogni pagina che si aggiungerà sotto.
    path: '/',
    component: () => import('@/components/layout/ClientLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'search',
        component: () => import('@/views/SearchView.vue'),
        meta: { title: 'routes.search' },
      },
      {
        // L'assistente: i sintomi scritti a parole proprie, le indicazioni dalle voci.
        path: 'assistente',
        name: 'assistant',
        component: () => import('@/views/AssistantView.vue'),
        meta: { title: 'routes.assistant' },
      },
      {
        // La valutazione di doc/schema.md: più voci con data e lato, il risultato in tre parti.
        path: 'valutazione',
        name: 'evaluation',
        component: () => import('@/views/EvaluationView.vue'),
        meta: { title: 'routes.evaluation' },
      },
      {
        // Il pid nell'URL: la scheda si può ricaricare, salvare nei preferiti e condividere
        // con un altro cliente (che dovrà comunque essere collegato per vederla).
        path: 'sintomo/:pid',
        name: 'report',
        component: () => import('@/views/SymptomReportView.vue'),
        meta: { title: 'routes.report' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/errors/NotFoundView.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: (_to, _from, savedPosition) => savedPosition ?? { top: 0 },
})

router.beforeEach((to) => {
  const authStore = useAuthStore()
  // Un token scaduto vale come nessun token, e va deciso PRIMA che la pagina si monti.
  authStore.dropExpiredSession()
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (to.name === 'login' && authStore.isAuthenticated) {
    return { name: 'search' }
  }
  const appTitle = i18n.global.t('app.name')
  document.title =
    typeof to.meta.title === 'string' ? `${i18n.global.t(to.meta.title)} — ${appTitle}` : appTitle
})

export default router
